import { computed, ref } from 'vue';
import { useQueries } from '@tanstack/vue-query';
import { flatMap } from 'lodash-es';
import type { flightEntity } from 'freedom-shared';
import type { PassengersData, PassengerKey } from '@aviata/booking-data';

import type { SelectedSeatsState } from '@/widgets/seat-map';

import { seatMapApi } from './api';
import {
  getPassengersDataWithoutInfants,
  separateByFlights,
  getSeatMapInitialStateByFlights,
} from './lib';

type UseSeatMap = {
  offerId: string;
  mappedFlights: flightEntity.types.MappedFlight[];
  passengersData: PassengersData;
  initialState?: SelectedSeatsState;
};

export function useSeatMap({
  offerId,
  mappedFlights,
  passengersData,
  initialState,
}: UseSeatMap) {
  const allFlightsSegmentsIdx = flatMap(mappedFlights, v => v.segments).map(
    (_, i) => i,
  );
  const passengersDataWithoutInfants =
    getPassengersDataWithoutInfants(passengersData);
  const passengersKeys = Object.keys(
    passengersDataWithoutInfants,
  ) as PassengerKey[];
  const seatMapStateByFlight = ref(
    getSeatMapInitialStateByFlights({
      mappedFlights,
      passengers: passengersDataWithoutInfants,
      allFlightsSegmentsIdx,
      initialState,
    }),
  );
  const seatMapStateBySegment = computed(() =>
    seatMapStateByFlight.value.flatMap(state => state),
  );

  const queries = computed(() =>
    allFlightsSegmentsIdx.map(idx => {
      return {
        queryKey: ['seatMaps', offerId, idx],
        queryFn: () => seatMapApi.fetchSeatMap(offerId, idx.toString()),
        refetchOnWindowFocus: false,
      };
    }),
  );
  const seatMapQueries = useQueries({ queries });

  function getFlightQueries(
    flight: flightEntity.types.MappedFlight,
    flightIdx: number,
  ) {
    return separateByFlights({
      mappedFlights,
      separableEntity: seatMapQueries,
      flightIdx,
      flight,
    });
  }

  function getFlightState(flightIdx: number) {
    return seatMapStateByFlight.value[flightIdx];
  }

  function updateSeatMapState(
    updatedSeatMapStateByFlight: SelectedSeatsState,
    flightIdx: number,
  ) {
    seatMapStateByFlight.value[flightIdx] = updatedSeatMapStateByFlight;
  }

  return {
    seatMapQueries,
    passengersData: passengersDataWithoutInfants,
    passengersKeys,
    seatMapStateBySegment,
    allFlightsSegmentsIdx,
    seatMapStateByFlight,
    getFlightQueries,
    getFlightState,
    updateSeatMapState,
  };
}
